import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@app/environments/environment';
import { CreativesetDataService } from '@app/shared/creativeset/creativeset.data.service';
import { CampaignService } from '@app/shared/services';
import { VersionsService } from '@app/shared/versions/state/versions.service';
import { UIDialogService, UINotificationService } from '@bannerflow/ui';
import { ICreative } from '@studio/domain/creativeset/creative/creative';
import { TrackerService } from '@studio/monitoring/browser';
import {
    EventLoggerService,
    PublishChangesEndEvent,
    PublishChangesEvent
} from '@studio/monitoring/browser/events';
import { Logger } from '@studio/utils/logger';
import { firstValueFrom, lastValueFrom, Subject, timer } from 'rxjs';
import { last, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import {
    ICampaignPublishInfo,
    IPublishInfo,
    IZipOrchestrationData,
    IZipOrchestrationResponse,
    PublishStatus
} from '@studio/domain/campaign';
import { PublishChangesDialogComponent } from '../publish-changes-dialog/publish-changes-dialog.component';
import { ChangeState, CreativeMutation } from './creative-mutation';
import { TileSelectService } from './tile-select.service';

@Injectable({ providedIn: 'root' })
export class PublishService {
    private logger = new Logger('PublishService');
    private unsubscribe$ = new Subject<void>();
    private account: string;
    private brand: string;
    private publishedCreatives: IPublishInfo[] = [];

    publish = new CreativeMutation<ICreative[]>();
    private publish$ = new Subject<void>();
    dialogIsOpen = false;
    private campaignStatusPolling$ = new Subject<void>();
    private isPublishing = false;

    constructor(
        private creativesetDataService: CreativesetDataService,
        private tileSelectService: TileSelectService,
        private dialogService: UIDialogService,
        private http: HttpClient,
        private notificationService: UINotificationService,
        private eventLogger: EventLoggerService,
        private campaignService: CampaignService,
        private versionsService: VersionsService
    ) {
        this.account = this.creativesetDataService.getAccountSlug();
        this.brand = this.creativesetDataService.getBrandSlug();

        this.campaignService.creativesPublished$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(creatives => {
                this.publishedCreatives = creatives;
            });

        this.publish$
            .pipe(
                tap(() => (this.isPublishing = true)),
                switchMap(() => {
                    if (this.campaignStatusPolling$.closed) {
                        this.campaignStatusPolling$ = new Subject<void>();
                    }

                    const pollingAmount = 60;
                    const pollingInterval = 3000;
                    const interval$ = timer(0, pollingInterval).pipe(take(pollingAmount));

                    interval$.pipe(last(), takeUntil(this.campaignStatusPolling$)).subscribe(() => {
                        this.logger.log(
                            `Polling ended. It was either cancelled or reached the maximum amount of allowed pollings.`
                        );
                        this.finishPendingPublish();
                    });

                    return interval$.pipe(
                        tap(i =>
                            this.logger.log(`Polling campaign status after ${i * pollingInterval}ms`)
                        ),
                        switchMap(() => this.campaignService.makeCampaignRequest()),
                        takeUntil(this.campaignStatusPolling$)
                    );
                })
            )
            .subscribe(() => {
                const failedCreatives = this.failedPublishedCreatives();
                const allCreativesPublished = this.allCreativesPublished();

                if (failedCreatives.length) {
                    const creativeIDs = failedCreatives.reduce((prev, cur) => `${prev} ${cur.id}`, '');
                    this.logger.warn(`Creatives {${creativeIDs} } failed to publish!`);
                }

                if (allCreativesPublished) {
                    this.logger.log('All creatives were successfully pushed!');
                }

                if (
                    allCreativesPublished ||
                    failedCreatives.length === this.publishedCreatives.length
                ) {
                    this.finishPendingPublish();
                    this.eventLogger.log(new PublishChangesEndEvent());
                }
            });
    }

    private finishPendingPublish(): void {
        if (!this.campaignStatusPolling$.closed) {
            this.campaignStatusPolling$.next();
        }
        const publishedCreatives = this.publishedCreatives.map(pc => ({ ...pc, loading: false }));

        this.campaignService.updateCreativePublishInfo(publishedCreatives);

        this.publish.emit(
            publishedCreatives.map(pc => pc.creative),
            ChangeState.FINISHED
        );
        this.isPublishing = false;
    }

    publishCreatives(creative?: ICreative): void {
        let selectedCreatives = creative ? [creative] : this.tileSelectService.getSelected();

        if (selectedCreatives.length === 0) {
            const creativeset = this.creativesetDataService.creativeset;
            selectedCreatives = [...creativeset.creatives];
        }

        const creativesToPublish: ICreative[] = selectedCreatives.filter(selectedCreative =>
            this.isPublishable(selectedCreative)
        );

        if (creativesToPublish.length) {
            const campaigns = this.campaignService.getCampaigns();

            const dialog = this.dialogService.openComponent(PublishChangesDialogComponent, {
                headerText: 'Push changes to campaign',
                theme: 'default',
                width: '700px',
                data: {
                    creatives: creativesToPublish,
                    campaigns: campaigns.filter(c => c.publishStatus.status === PublishStatus.Published)
                }
            });

            dialog
                .afterClose()
                .pipe(take(1))
                .subscribe(async () => {
                    if (dialog.config.data.confirmed) {
                        const creativeIDs = creativesToPublish.reduce(
                            (prev, cur) => `${prev} ${cur.id}`,
                            ''
                        );
                        this.logger.log(`Publishing {${creativeIDs} } creatives!`);

                        await this.publishChanges(creativesToPublish);
                    }
                });
        }
    }

    cancelPublish(): void {
        this.finishPendingPublish();
    }

    isPublishable = (creative: ICreative): boolean => {
        if (!this.campaignService.isCreativeConnectedToCampaign(creative)) {
            return false;
        }

        const creativePublishInfo = this.campaignService.getCreativePublishInfo(creative);

        if (!creativePublishInfo) {
            return false;
        }

        let canBePublished = false;
        for (const campaign of creativePublishInfo.connectedCampaigns) {
            // Creative is published to at least one campaign and is in a publishable state
            const status = campaign.publishStatus.status;
            if (
                campaign &&
                status !== PublishStatus.Publishing &&
                status !== PublishStatus.NotPublished
            ) {
                canBePublished = true;
                break;
            }
        }

        if (!canBePublished) {
            return false;
        }

        /**
         * Failed published campaigns should be able to republish.
         * Checksum that mismatch with campaign should be publishable.
         **/
        if (
            creativePublishInfo.connectedCampaigns.find(
                campaign =>
                    campaign.publishStatus.failed || this.hasPublishableChecksum(campaign, creative)
            )
        ) {
            return true;
        }

        return false;
    };

    private hasPublishableChecksum(campaign: ICampaignPublishInfo, creative: ICreative): boolean {
        // checksum === null means ad for this creative is not published. Hence, gray rocket will be shown
        return !!campaign.checksum && campaign.checksum !== creative.checksum;
    }

    async publishChanges(creativesToPublish: ICreative[]): Promise<void> {
        if (this.isPublishing) {
            return;
        }

        this.eventLogger.log(new PublishChangesEvent());

        this.publish.emit(creativesToPublish, ChangeState.PENDING);

        const creativeset = this.creativesetDataService.creativeset;

        this.publishedCreatives = this.publishedCreatives.map(pi => ({
            ...pi,
            loading: creativesToPublish.some(creative => creative.id === pi.creative.id)
        }));

        // Emit publish info in a loading state
        this.campaignService.updateCreativePublishInfo(this.publishedCreatives);

        try {
            this.isPublishing = true;
            TrackerService.captureMetric('Published Creatives', creativesToPublish.length);
            const publishResult = await firstValueFrom(
                this.creativesetDataService.publishChanges(creativesToPublish)
            );

            // Update creative's checksum
            publishResult.creatives.forEach(publishCreative => {
                const target = creativeset.creatives.find(c => c.id === publishCreative.id);
                if (target) {
                    target.checksum = publishCreative.checksum;
                } else {
                    this.logger.warn('Publish result has different creatives');
                }
            });

            this.publish$.next();
        } catch (e) {
            this.finishPendingPublish();
        }
    }

    failedPublishedCreatives(): ICreative[] {
        return this.publishedCreatives
            .filter(creative => !!creative.connectedCampaigns.find(c => c.publishStatus.failed))
            .map(pc => pc.creative);
    }

    allCreativesPublished(): boolean {
        if (this.publishedCreatives.length === 0) {
            return false;
        }
        return this.publishedCreatives
            .filter(publishInfo => publishInfo.loading)
            .every(publishInfo => !this.isPublishable(publishInfo.creative));
    }

    async redirectToPublish(creatives: ICreative[]): Promise<void> {
        const versions = await firstValueFrom(this.versionsService.versions$);

        const orcApi = `${environment.origins.zipOrchestration}/api/${this.account}/${this.brand}`;
        const creativeset = this.creativesetDataService.creativeset;

        await firstValueFrom(this.creativesetDataService.publishChanges(creatives));

        const zipCreativesData: IZipOrchestrationData[] = creatives.map(creative => {
            const version = versions.find(v => v.id === creative.version.id)!;
            return {
                creativeId: creative.id,
                creativesetId: creativeset.id,
                localizationId: version.localization.id,
                size: {
                    width: creative.size.width,
                    height: creative.size.height
                },
                targetUrl: creative.targetUrl || version.targetUrl!,
                checksum: creative.checksum!
            };
        });

        try {
            const { correlationId } = await lastValueFrom(
                this.http.post<IZipOrchestrationResponse>(`${orcApi}/Orchestration`, zipCreativesData)
            );

            const url = `${environment.origins.campaignManager}/${this.account}/${this.brand}/publish?correlationId=${correlationId}`;

            window.open(url, '_blank');
        } catch (e) {
            this.logger.error(e);
            this.notificationService.open('Could not export to HTML', {
                autoCloseDelay: 5000,
                placement: 'top',
                type: 'error'
            });
        }
    }
}
